import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { KeyRound, X } from 'lucide-react';
import formatApiError from '../../../utils/formatApiError';
import ServiceAccountAccessSteps, { serviceAccountLabel } from '../components/ServiceAccountAccessSteps';
import '../AIGarage.css';

const GrantSecScanAccessModal = ({
  isOpen,
  repo,
  serviceUser,
  onClose,
  onDone,
}) => {
  const [token, setToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setToken('');
    setError(null);
  }, [isOpen, repo?.id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose, submitting]);

  if (!isOpen || !repo) return null;

  const userLabel = serviceAccountLabel(serviceUser);

  const handleDone = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await onDone((token || '').trim() || undefined);
      if (updated?.accessStatus === 'verified') {
        return;
      }
      if (updated?.accessStatus === 'pending' || updated?.accessStatus === 'checking') {
        return;
      }
      setError(
        updated?.accessMessage
        || 'Access is still missing. Grant the service user and try again, or paste a token.'
      );
    } catch (err) {
      setError(formatApiError(err, 'Could not verify access'));
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="garage-modal-overlay" onClick={!submitting ? onClose : undefined}>
      <div
        className="garage-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sec-scan-grant-access-title"
      >
        <div className="garage-modal-header">
          <div className="garage-modal-title-row">
            <div className="garage-modal-icon">
              <KeyRound size={22} />
            </div>
            <h2 id="sec-scan-grant-access-title">Grant repository access</h2>
          </div>
          <button
            className="garage-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <p className="garage-confirm-message">
            {userLabel} cannot read{' '}
            <strong>{repo.displayName || repo.gitUrl}</strong>. Scanning stays
            disabled until access is verified.
          </p>
          <ServiceAccountAccessSteps serviceUser={serviceUser} />

          <div className="garage-form-group">
            <label htmlFor="sec-scan-access-token">
              Access token <span className="garage-optional">(Optional)</span>
            </label>
            <input
              id="sec-scan-access-token"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Leave blank to re-check the service account (Reporter)"
              disabled={submitting}
            />
            <p className="garage-field-hint">
              Encrypted in the browser before upload, then stored encrypted for
              scans only. Never shown again.
            </p>
          </div>

          {error && (
            <div className="garage-error-message" role="alert">{error}</div>
          )}
        </div>

        <div className="garage-modal-actions">
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Close
          </button>
          <button
            type="button"
            className="garage-btn garage-btn-primary"
            onClick={handleDone}
            disabled={submitting}
          >
            {submitting ? 'Checking…' : 'Done'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GrantSecScanAccessModal;
